import styled from '@emotion/styled';
import { Breakpoints } from '@settings';
import React, { ReactNode } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export type SwatchSizes = 'sm' | 'md';

type StyledProps = {
  size?: SwatchSizes;
  active?: boolean;
  isAvailable?: boolean;
};

type Props = {
  swatch: {
    id: number | string;
    maxAdditionalSalePercent?: number;
    isAvailable: boolean;
    isInStock: boolean;
    formattedValue: string;
  };
  indicateSale: boolean;
  itemHandler: any;
  extra?: any;
} & Omit<StyledProps, 'disabled'>;

const StyledSwatch = styled.a<StyledProps>`
  color: ${(props) => (props.active ? 'var(--swatch-text-color-active)' : 'var(--swatch-text-color)')};
  font-weight: var(--swatch-font-weight);
  transition: 0.2s;
  cursor: pointer;
  background-color: ${(props) => (props.active ? 'var(--swatch-bg-color-active)' : 'var(--swatch-bg-color)')};
  border-width: ${(props) =>
    props.active ? 'var(--swatch-border-width-active)' : 'var(--swatch-border-width)'};
  border-style: solid;
  border-color: ${(props) =>
    props.active ? 'var(--swatch-border-color-active)' : 'var(--swatch-border-color)'};
  text-decoration: none !important;
  min-width: 45px;
  border-radius: var(--swatch-border-radius);
  padding-left: ${(props) => `var(--swatch-padding-x-${props.size}${props.active ? '-active' : ''})`};
  padding-right: ${(props) => `var(--swatch-padding-x-${props.size}${props.active ? '-active' : ''})`};
  font-size: 14px;
  height: ${(props) => `var(--swatch-height-${props.size})`};
  width: ${(props) => `var(--swatch-width-${props.size})`};
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-text-stroke-width: ${(props) => (props.active ? 'var(--swatch-text-stroke-hover)' : '0')};

  &:hover {
    color: ${(props) =>
      props.active ? 'var(--swatch-text-color-active)' : 'var(--swatch-text-color-hover)'};
    background-color: ${(props) =>
      props.active ? 'var(--swatch-bg-color-active)' : 'var(--swatch-bg-color-hover)'};
    border-width: ${(props) =>
      props.active ? 'var(--swatch-border-width-active)' : 'var(--swatch-border-width-hover)'};
    border-color: ${(props) =>
      props.active ? 'var(--swatch-border-color-active)' : 'var(--swatch-border-color-hover)'};
    -webkit-text-stroke-width: var(--swatch-text-stroke-hover);
    text-decoration: none !important;
    padding-left: ${(props) => `var(--swatch-padding-x-${props.size}-active)`};
    padding-right: ${(props) => `var(--swatch-padding-x-${props.size}-active)`};
  }

  @media (max-width: ${Breakpoints.MD}px) {
    padding-left: ${(props) => `var(--swatch-padding-x-${props.size}-mob)`};
    padding-right: ${(props) => `var(--swatch-padding-x-${props.size}-mob)`};
    height: ${(props) => `var(--swatch-height-${props.size}-mob)`};
    font-size: 13px;

    &:hover {
      padding-left: ${(props) => `var(--swatch-padding-x-${props.size}-mob-active)`};
      padding-right: ${(props) => `var(--swatch-padding-x-${props.size}-mob-active)`};
    }
  }
`;

const StyledSwatchDisabled = styled.div<Omit<StyledProps, 'disabled'>>`
  color: ${({ active }) => (active ? 'var(--swatch-text-color-active)' : 'var(--swatch-text-color)')};
  font-weight: var(--swatch-font-weight);
  cursor: ${({ isAvailable }) => (isAvailable ? 'pointer' : 'not-allowed')};
  background-color: var(--swatch-bg-color);
  border-width: var(--swatch-border-width-active);
  border-style: solid;
  border-color: ${({ active }) => (active ? 'var(--swatch-border-color-active)' : '#bbb')};
  position: relative;
  overflow: hidden;
  min-width: 50px;
  padding-left: ${(props) => `var(--swatch-padding-x-${props.size})`};
  padding-right: ${(props) => `var(--swatch-padding-x-${props.size})`};
  font-size: 14px;
  height: ${(props) => `var(--swatch-height-${props.size})`};
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none !important;
  border-radius: var(--swatch-border-radius);
  -webkit-text-stroke-width: ${(props) => (props.active ? 'var(--swatch-text-stroke-hover)' : '0')};

  ::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: linear-gradient(
      to top left,
      transparent calc(50% - 2px),
      ${({ active }) => (active ? 'var(--swatch-border-color-active)' : '#bbb')} calc(50% - 1px),
      ${({ active }) => (active ? 'var(--swatch-border-color-active)' : '#bbb')} calc(50% + 1px),
      transparent calc(50% + 2px)
    );
  }

  &:hover {
    color: ${(props) =>
      props.active ? 'var(--swatch-text-color-active)' : 'var(--swatch-text-color-hover)'};
    background-color: ${(props) =>
      props.active ? 'var(--swatch-bg-color-active)' : 'var(--swatch-bg-color-hover)'};
    border-width: ${(props) =>
      props.active ? 'var(--swatch-border-width-active)' : 'var(--swatch-border-width-hover)'};
    border-color: ${(props) =>
      props.active ? 'var(--swatch-border-color-active)' : 'var(--swatch-border-color-hover)'};
    -webkit-text-stroke-width: var(--swatch-text-stroke-hover);
    text-decoration: none !important;
    padding-left: ${(props) => `var(--swatch-padding-x-${props.size}-active)`};
    padding-right: ${(props) => `var(--swatch-padding-x-${props.size}-active)`};

    ::before {
      background: linear-gradient(
        to top left,
        transparent calc(50% - 2px),
        ${({ active }) => (active ? 'var(--swatch-border-color-active)' : 'var(--swatch-border-color-hover)')}
          calc(50% - 1px),
        ${({ active }) => (active ? 'var(--swatch-border-color-active)' : 'var(--swatch-border-color-hover)')}
          calc(50% + 1px),
        transparent calc(50% + 2px)
      );
    }
  }

  @media (max-width: ${Breakpoints.MD}px) {
    padding-left: ${(props) => `var(--swatch-padding-x-${props.size}-mob)`};
    padding-right: ${(props) => `var(--swatch-padding-x-${props.size}-mob)`};
    height: ${(props) => `var(--swatch-height-${props.size}-mob)`};
    font-size: 13px;
  }
`;

const Swatch = ({ size = 'md', swatch, indicateSale, itemHandler, active, extra }: Props) => {
  const swatchHref = (swatch) => (active ? '#' : swatch.url);

  const changeItemHandler = (e) => {
    e.preventDefault();

    itemHandler(swatch)();
  };

  if (!swatch.isAvailable)
    return (
      <div className='tw:relative tw:inline-block'>
        <StyledSwatchDisabled size={size}>
          <span>{swatch.formattedValue}</span>
        </StyledSwatchDisabled>
      </div>
    );

  const StyledComponent = swatch.isInStock ? StyledSwatch : StyledSwatchDisabled;

  return (
    <HoverOverlayTrigger
      noHoverEffect={!extra?.tagline || extra?.noTooltip}
      overlay={<Tooltip>{extra?.tagline}</Tooltip>}>
      <div className='tw:relative tw:inline-block'>
        <StyledComponent
          active={active}
          size={size}
          onClick={changeItemHandler}
          href={swatchHref(swatch)}
          isAvailable={swatch.isAvailable}>
          <span>{swatch.formattedValue}</span>
        </StyledComponent>
      </div>
    </HoverOverlayTrigger>
  );
};

const HoverOverlayTrigger = ({ noHoverEffect, overlay, children }) => {
  if (noHoverEffect) return children;

  return (
    <OverlayTrigger overlay={overlay} placement={'bottom'}>
      {children}
    </OverlayTrigger>
  );
};

export default Swatch;
